/*
 *
 *  * Copyright(c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import { Injectable } from '@angular/core';
import { CacheFactoryService } from '@app/ui/client-cache/factory/cache-factory.service';
import { ClinicalCacheFactory } from '@app/ui/client-cache/factory/clinical-cache-factory';
import { MessageService } from '@core/lib/services';

@Injectable({
  providedIn: 'root'
})
export class ClinicalInfoService {
  private readonly clinicalInfoCache: ClinicalCacheFactory;
  private statusUpdateEvent = 'statusUpdate';
  private priorityUpdateEvent = 'priorityUpdate';
  private statusChangeStartEvent = 'statusChangeStart';

  constructor(private cacheFactoryService: CacheFactoryService, private messageService: MessageService) {
    this.clinicalInfoCache = this.cacheFactoryService.createClinicalCacheFactory(
      'clinicalInfoCache',
      this.getEndpointFunction(),
      this.comparator
    );
    this.messageService.on('refreshDXHub').subscribe(data => {
      this.clinicalInfoCache.refreshScenario = data.refresh;
    });
  }

  /**
   * @name getClinicalInfo
   * @desc Fetches clinical info
   * @param {string|number} id.  Identifier to be fetched
   * @param {boolean} refreshIfCached. Whether or not to do a refresh if the value is cached, default is true
   * @param {object} sirccRequestParams request params
   */
  getClinicalInfo(id, refreshIfCached, sirccRequestParams = undefined) {
    return this.clinicalInfoCache.get(id, refreshIfCached, sirccRequestParams);
  }

  /**
   * @name getRemoteClinicalInfo
   * @desc Fetches remote study clinical info
   * @param {string|number} id.  Identifier to be fetched
   */
  getRemoteClinicalInfo(id) {
    return this.clinicalInfoCache.getRemoteStudyCache(id);
  }

  /**
   * @name setClinicalInfo
   * @desc Stores clinical info
   * @param {string|number} id.  Identifier to be stored
   * * @param {any} data.  Data to be stored
   */
  setClinicalInfo(id: string | number, data: any) {
    return this.clinicalInfoCache.updateCache(id, data);
  }

  /**
   * @name removeClinicalInfo
   * @desc removes clinical info from cache
   * @param {string|number} id. Identifier to be removed from cache
   */
  removeClinicalInfo(id) {
    return this.clinicalInfoCache.remove(id);
  }

  /**
   * @name onStatusUpdate
   * @desc Adds listener for when the status is updated.
   * @param {function} callback. Handler for status change.
   * @param {object} context. Optional Context object to use when calling the handler.
   */
  onStatusUpdate(callback, context?) {
    return this.clinicalInfoCache.on(this.statusUpdateEvent, callback, context);
  }

  onStatusChangeStart(callback, context) {
    return this.clinicalInfoCache.on(this.statusChangeStartEvent, callback, context);
  }

  refreshSingleFieldOnStatusUpdate(data) {
    this.clinicalInfoCache.updateSingleFieldInCache(data);
    this.clinicalInfoCache.fireEvent(this.statusUpdateEvent, data.requestedProcedureId, data.status, data.statusId);
  }

  examStatusChangeStart(data) {
    this.clinicalInfoCache.fireEvent(this.statusChangeStartEvent, data);
  }

  updateClinicalInfoCache(data) {
    this.clinicalInfoCache.updateSingleFieldInCache(data);
  }

  /**
   * @name onPriorityUpdate
   * @desc Adds listener for when the priority is updated.
   * @param {function} callback. Handler for priority change.
   * @param {object} context. Optional Context object to use when calling the handler.
   */
  onPriorityUpdate(callback, context?) {
    return this.clinicalInfoCache.on(this.priorityUpdateEvent, callback, context);
  }

  refreshSingleFieldOnPriorityUpdate(data) {
    this.clinicalInfoCache.updateSingleFieldInCache(data);
    this.clinicalInfoCache.fireEvent(
      this.priorityUpdateEvent,
      data.requestedProcedureId,
      data.priorityId,
      data.priority
    );
  }

  updateWorkItemState(id, state) {
    this.clinicalInfoCache.updateWorkItemState(id, state);
  }

  getClinicalInfoCache(): ClinicalCacheFactory {
    return this.clinicalInfoCache;
  }

  /************************** Private Functions **************************/
  private getEndpointFunction() {
    const endpointTemplate = 'requested-procedures/{id}/clinical-info';
    const endpointNeedle = '{id}';
    return id => {
      return endpointTemplate.replace(endpointNeedle, id);
    };
  }

  private statusHasChanged(cachedValue, serverValue) {
    const statusEquality = cachedValue.requestedProcedure.status !== serverValue.requestedProcedure.status;
    const statusIdEquality = cachedValue.requestedProcedure.statusId !== serverValue.requestedProcedure.statusId;
    return statusEquality || statusIdEquality;
  }

  private comparator = (requestedProcedureId, cachedValue, serverValue) => {
    if (cachedValue.requestedProcedure.priorityId !== serverValue.requestedProcedure.priorityId) {
      this.clinicalInfoCache.fireEvent(
        this.priorityUpdateEvent,
        requestedProcedureId,
        serverValue.requestedProcedure.priorityId,
        serverValue.requestedProcedure.priority
      );
    }

    if (this.statusHasChanged(cachedValue, serverValue)) {
      const requestedProcedure = serverValue.requestedProcedure;
      this.clinicalInfoCache.fireEvent(
        this.statusUpdateEvent,
        requestedProcedureId,
        requestedProcedure.status,
        requestedProcedure.statusId
      );
    }
    //Update cached data with server data
    cachedValue.requestedProcedure = serverValue.requestedProcedure;
    cachedValue.patient = serverValue.patient;
    cachedValue.diagnosticReport = serverValue.diagnosticReport;
    cachedValue.note = serverValue.note;
    cachedValue.practitioner = serverValue.practitioner;
  };
}
